import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from './apiClient';

// Fetch Benefits by ICT ID
export const fetchBenefitsByICTId = createAsyncThunk(
  'benefits/fetchBenefitsByICTId',
  async (ictId, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get(`/benefits/ict/${ictId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch benefits');
    }
  }
);

// Insert New Benefit
export const insertBenefit = createAsyncThunk(
  'benefits/insertBenefit',
  async (benefitData, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post('/benefits', benefitData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to insert benefit');
    }
  }
);

// Update Benefit Description
export const updateBenefitDesc = createAsyncThunk(
  'benefits/updateBenefitDesc',
  async ({ id, benefitDesc }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.patch(`/benefits/${id}/desc`, { benefitDesc });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update benefit description');
    }
  }
);

// Delete Benefit
export const deleteBenefit = createAsyncThunk(
  'benefits/deleteBenefit',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/benefits/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete benefit');
    }
  }
);

const benefitsSlice = createSlice({
  name: 'benefits',
  initialState: {
    benefits: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchBenefitsByICTId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBenefitsByICTId.fulfilled, (state, action) => {
        state.loading = false;
        state.benefits = action.payload;
      })
      .addCase(fetchBenefitsByICTId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // INSERT
      .addCase(insertBenefit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertBenefit.fulfilled, (state, action) => {
        state.loading = false;
        state.benefits.push(action.payload);
      })
      .addCase(insertBenefit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateBenefitDesc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBenefitDesc.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const idx = state.benefits.findIndex((b) => b.id === updated.id);
        if (idx !== -1) state.benefits[idx] = updated;
      })
      .addCase(updateBenefitDesc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteBenefit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBenefit.fulfilled, (state, action) => {
        state.loading = false;
        state.benefits = state.benefits.filter((b) => b.id !== action.payload);
      })
      .addCase(deleteBenefit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default benefitsSlice.reducer;
